import { getOpportunityTemplateById, createOpportunityTemplate } from "@fp/admin-api-client/client";
import {
	PageContentContainer,
	PageProgressBar,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { useSetPageActions } from "@/components/page-actions-context";
import { PageActionsMenu } from "@/components/page-actions-menu";
import { TextInputModal } from "@fp/ui/components/application/modals/text-input-modal";
import { File06 } from "@untitledui/icons";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { z } from "zod";
import { CreateOppPageContent, type OpportunityFormContext } from "@fp/ui/components/opportunities/opportunity-form";
import {
	getOpportunityById,
	OpportunityStatus,
	type OpportunityDto,
} from "@fp/admin-api-client";
import type { OpportunityTemplateDto } from "@fp/admin-api-client/types/opportunityTemplateDto.js";

const newOppSearchSchema = z.object({
	template: z.string().optional(),
	oppId: z.string().optional(),
});

const mapTemplateToOpportunity = (template: OpportunityTemplateDto): Partial<OpportunityDto> => ({
	title: template.title,
	description: template.description,
	type: template.type,
	requireGpsCheckIn: template.requireGpsCheckIn,
	enableGpsCheckIn: template.enableGpsCheckIn,
	requireImage: template.requireImage,
	requireDescription: template.requireDescription,
	requireTimeSheet: template.requireTimeSheet,
	referenceRequirements: template.referenceRequirements,
	payoutInstructions: template.payoutInstructions,
	startDate: template.startDate,
	endDate: template.endDate,
	payRate: template.payRate,
	numberOfUnits: template.numberOfUnits,
	maxUnitsPerParticipant: template.maxUnitsPerParticipant,
	maxClaimsPerRoster: template.maxClaimsPerRoster,
	address: template.address,
	status: OpportunityStatus.Draft,
} as Partial<OpportunityDto>);

export const Route = createFileRoute("/opps/new")({
	validateSearch: newOppSearchSchema,
	loaderDeps: ({ search }) => ({
		templateId: search.template,
		oppId: search.oppId,
	}),
	component: RouteComponent,
	loader: async ({ deps: { templateId, oppId } }) => {
		if (templateId) {
			const templateResult = await getOpportunityTemplateById(templateId);
			if (!templateResult.data) {
				throw new Error("Template not found.");
			}
			return mapTemplateToOpportunity(templateResult.data);
		}
		if (oppId) {
			const opportunityResult = await getOpportunityById(oppId);
			if (!opportunityResult.data) {
				throw new Error("Opportunity not found.");
			}
			return opportunityResult.data;
		}
		return {} as Partial<OpportunityDto>;
	},
});

function RouteComponent() {
	const opportunity = Route.useLoaderData();
	const router = useRouter();
	const formRef = useRef<OpportunityFormContext | null>(null);
	const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
	const [isSavingTemplate, setIsSavingTemplate] = useState(false);

	useSetPageActions("Create an Opp", useMemo(() => [
		{ id: "save-as-template", icon: File06, label: "Save as Template", onAction: () => setShowSaveTemplateModal(true) },
	], []));

	const onSuccess = async (opportunity: OpportunityDto) => {
		await router.invalidate({ sync: true });
		router.navigate({
			to: "/opps/$oppId/edit/set-rates",
			params: { oppId: opportunity.id },
		});
	};

	const handleSaveAsTemplate = async (templateName: string) => {
		const values = formRef.current?.form.getValues();
		if (!values) return;
		setIsSavingTemplate(true);
		try {
			await createOpportunityTemplate({
				templateName,
				title: values.title,
				description: values.description,
				type: values.type || "Job",
				requireGpsCheckIn: values.requireGpsCheckIn,
				enableGpsCheckIn: values.enableGpsCheckIn,
				requireImage: false,
				requireDescription: false,
				requireTimeSheet: false,
				startDate: values.startDate ? new Date(values.startDate.year, values.startDate.month - 1, values.startDate.day, values.startTime?.hour || 0, values.startTime?.minute || 0).toISOString() : undefined,
				endDate: values.expirationDate ? new Date(values.expirationDate.year, values.expirationDate.month - 1, values.expirationDate.day, values.expirationTime?.hour || 0, values.expirationTime?.minute || 0).toISOString() : undefined,
				payRate: values.payRate,
				numberOfUnits: values.numberOfUnits,
				maxClaimsPerRoster: values.maxClaimsPerRoster,
				address: values.includeAddress ? { addressLine1: values.address.street, city: values.address.city, state: values.address.state, zip: values.address.zipCode, country: "US" } : undefined,
				isShareable: false,
				isActive: true,
			});
			setShowSaveTemplateModal(false);
			toast.success("Template saved");
		} catch {
			toast.error("Failed to save template");
		} finally {
			setIsSavingTemplate(false);
		}
	};

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Create an Opp"
				parent={{ url: "/opps", label: "Opps" }}
				titleActions={<PageActionsMenu />}
				footer={<PageProgressBar current={0} total={4} />}
			/>
			<PageContentContainer className="space-y-6">
				<CreateOppPageContent opportunity={opportunity} onSuccess={onSuccess} formRef={formRef} />
			</PageContentContainer>
			<TextInputModal
				isOpen={showSaveTemplateModal}
				onClose={() => setShowSaveTemplateModal(false)}
				onConfirm={handleSaveAsTemplate}
				title="Save as Template"
				message="Save the current form as a reusable template for your organization."
				label="Template Name"
				placeholder="e.g. Weekend Delivery Driver"
				confirmText="Save Template"
				isLoading={isSavingTemplate}
			/>
		</AdminLayout>
	);
}